import { Component, inject, input, OnChanges, SimpleChanges } from '@angular/core';
import { ZiroFieldAbstractDirective } from '../../../../../forms/ziro-field-abstract.directive';
import { NaturalLanguageEditorDoublePrimaryDropdownOption } from '../../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-double/natural-language-editor-double.component';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  AssignedCallingDelegate,
  ForwardingTargetType,
  TeamsCallingCallPickupGroup,
  UnansweredDelayInSeconds,
  UnansweredTargetType,
} from '../../../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { NaturalLanguageSingleOption } from '../../../../../shared/natural-language-editor/natural-language-editor-keyphrase/natural-language-editor-single/natural-language-editor-single.component';
import { cloneDeep } from 'lodash';
import { UnansweredCallForwardingSettingsTargetType } from '../unanswered-call-forwarding-settings.component';
import {
  MicrosoftTeamsUserSearchResource,
  TeamsUserSearchScope,
} from '../../../../shared/resources/microsoft-teams-user-search.resource';

interface UnansweredRulesFieldData {
  unansweredTarget: {
    primary: UnansweredCallForwardingSettingsTargetType;
    secondary: string;
  };
  unansweredDelay: string;
}

export interface UnansweredRulesEntity {
  unansweredTargetType: UnansweredTargetType;
  unansweredTarget: string;
  unansweredDelayInSeconds: UnansweredDelayInSeconds;
}

@Component({
  selector: 'ziro-teams-calling-unanswered-rules-field',
  templateUrl: 'teams-calling-unanswered-rules-field.component.html',
  providers: [{ provide: ZiroFieldAbstractDirective, useExisting: TeamsCallingUnansweredRulesFieldComponent }],
  standalone: false,
})
export class TeamsCallingUnansweredRulesFieldComponent
  extends ZiroFieldAbstractDirective<UnansweredRulesEntity, UnansweredRulesFieldData>
  implements OnChanges
{
  private translateService = inject(TranslateService);
  private _microsoftTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);

  readonly userPrincipalName = input<string>();
  readonly disabled = input<boolean>();
  readonly delegates = input<AssignedCallingDelegate[]>();
  readonly pickupGroups = input<TeamsCallingCallPickupGroup>();
  readonly forwardingTargetType = input<ForwardingTargetType>();
  readonly tenantId = input<string>();

  hasValidationError = false;

  UnansweredRulesFieldUnansweredTargetType = UnansweredCallForwardingSettingsTargetType;

  unansweredTargetTypeOptions: NaturalLanguageEditorDoublePrimaryDropdownOption[];
  unansweredDelayOptions: NaturalLanguageSingleOption[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pickupGroups?.currentValue) {
      this._setNaturalLanguageEditorConfig();
    }

    // trigger revalidation if the value of, "at least one delegate can receive calls," has changed.
    if (changes.delegates.currentValue && changes.delegates.previousValue) {
      const currentValidDelegates = changes.delegates.currentValue.some(
        (delegate: AssignedCallingDelegate) => delegate.receiveCallsEnabled,
      );
      const previousValidDelegates = changes.delegates.previousValue.some(
        (delegate: AssignedCallingDelegate) => delegate.receiveCallsEnabled,
      );
      if (currentValidDelegates !== previousValidDelegates) {
        this.fieldData = cloneDeep(this.fieldData);
      }
    }

    // trigger revalidation if the value of forwarding target type has changed.
    if (
      changes.forwardingTargetType?.currentValue &&
      changes.forwardingTargetType?.previousValue &&
      changes.forwardingTargetType.currentValue !== changes.forwardingTargetType.previousValue
    ) {
      this.fieldData = cloneDeep(this.fieldData);
    }
  }

  toEntity = (fieldData: UnansweredRulesFieldData): UnansweredRulesEntity => {
    return {
      unansweredTargetType: this._mapToEntityUnansweredTargetType(fieldData),
      unansweredTarget: fieldData.unansweredTarget.secondary || '',
      unansweredDelayInSeconds: this._mapToEntityUnansweredDelay(fieldData),
    };
  };

  toFieldData = (entity: UnansweredRulesEntity): UnansweredRulesFieldData => {
    return {
      unansweredTarget: {
        primary: this._mapToFieldDataUnansweredTargetType(entity),
        secondary: entity.unansweredTarget,
      },
      unansweredDelay: this._mapToFieldDataUnansweredDelay(entity),
    };
  };

  updateSelf(
    newFieldData: UnansweredRulesFieldData,
    markAsDirty = true,
    oldFieldData?: UnansweredRulesFieldData,
  ): void {
    const isUnansweredTargetTypeChangedFromNone =
      this.fieldData && this.fieldData.unansweredTarget.primary === UnansweredCallForwardingSettingsTargetType.NONE;
    if (isUnansweredTargetTypeChangedFromNone) {
      newFieldData.unansweredDelay = '10';
    }
    super.updateSelf(newFieldData, markAsDirty, oldFieldData);
  }

  updateValidationState(hasValidationError: boolean): void {
    this.hasValidationError = hasValidationError;
  }

  private _mapToFieldDataUnansweredTargetType(
    entity: UnansweredRulesEntity,
  ): UnansweredCallForwardingSettingsTargetType {
    switch (entity.unansweredTargetType) {
      case UnansweredTargetType.NONE:
        return UnansweredCallForwardingSettingsTargetType.NONE;
      case UnansweredTargetType.VOICEMAIL:
        return UnansweredCallForwardingSettingsTargetType.VOICEMAIL;
      case UnansweredTargetType.DELEGATES:
        return UnansweredCallForwardingSettingsTargetType.DELEGATES;
      case UnansweredTargetType.CALL_PICKUP_GROUP:
        return UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP;
      case UnansweredTargetType.SINGLE_TARGET:
        if (this.fieldData) {
          // fall back on this, there is no way to deduce whether it's a user or number if the field has already been touched
          return this.fieldData.unansweredTarget.primary;
        }
        return /^\+\d+$/.test(entity.unansweredTarget)
          ? UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER
          : UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER;
    }
  }

  private _mapToFieldDataUnansweredDelay(entity: UnansweredRulesEntity): string {
    switch (entity.unansweredDelayInSeconds) {
      case UnansweredDelayInSeconds.NONE:
        return '';
      case UnansweredDelayInSeconds.SECONDS_5:
        return '5';
      case UnansweredDelayInSeconds.SECONDS_10:
        return '10';
      case UnansweredDelayInSeconds.SECONDS_15:
        return '15';
      case UnansweredDelayInSeconds.SECONDS_20:
        return '20';
      case UnansweredDelayInSeconds.SECONDS_30:
        return '30';
      case UnansweredDelayInSeconds.SECONDS_40:
        return '40';
      case UnansweredDelayInSeconds.SECONDS_50:
        return '50';
      case UnansweredDelayInSeconds.SECONDS_60:
        return '60';
    }
  }

  private _mapToEntityUnansweredTargetType(fieldData: UnansweredRulesFieldData): UnansweredTargetType {
    switch (fieldData.unansweredTarget.primary) {
      case UnansweredCallForwardingSettingsTargetType.NONE:
        return UnansweredTargetType.NONE;
      case UnansweredCallForwardingSettingsTargetType.VOICEMAIL:
        return UnansweredTargetType.VOICEMAIL;
      case UnansweredCallForwardingSettingsTargetType.DELEGATES:
        return UnansweredTargetType.DELEGATES;
      case UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP:
        return UnansweredTargetType.CALL_PICKUP_GROUP;
      case UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER:
      case UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER:
        return UnansweredTargetType.SINGLE_TARGET;
    }
  }

  private _mapToEntityUnansweredDelay(fieldData: UnansweredRulesFieldData): UnansweredDelayInSeconds {
    if (fieldData.unansweredTarget.primary === UnansweredCallForwardingSettingsTargetType.NONE) {
      return UnansweredDelayInSeconds.NONE;
    }
    switch (fieldData.unansweredDelay) {
      case '5':
        return UnansweredDelayInSeconds.SECONDS_5;
      case '10':
        return UnansweredDelayInSeconds.SECONDS_10;
      case '15':
        return UnansweredDelayInSeconds.SECONDS_15;
      case '20':
        return UnansweredDelayInSeconds.SECONDS_20;
      case '30':
        return UnansweredDelayInSeconds.SECONDS_30;
      case '40':
        return UnansweredDelayInSeconds.SECONDS_40;
      case '50':
        return UnansweredDelayInSeconds.SECONDS_50;
      case '60':
        return UnansweredDelayInSeconds.SECONDS_60;
    }
  }

  private _searchForUsers(searchTerm: string): Observable<string[]> {
    return this._microsoftTeamsUserSearchResource
      .search(this.tenantId(), TeamsUserSearchScope.TEAMS_USER_MANAGEMENT, searchTerm)
      .pipe(map((results) => results.map((user) => user.userPrincipalName)));
  }

  private _setNaturalLanguageEditorConfig(): void {
    this.unansweredTargetTypeOptions = [
      {
        value: UnansweredCallForwardingSettingsTargetType.NONE,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.none',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.VOICEMAIL,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.voicemail',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_USER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.user',
        ),
        secondaryInputConfiguration: {
          type: 'search',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.placeholder',
          ),
          searchFn: (searchTerm) => this._searchForUsers(searchTerm),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.required_validator',
            },
            {
              validator: (val) => !val.secondary || val.secondary !== this.userPrincipalName(),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.user.not_self_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.SINGLE_TARGET_NUMBER,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.number',
        ),
        secondaryInputConfiguration: {
          type: 'text',
          label: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.label',
          ),
          placeholder: this.translateService.instant(
            'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.placeholder',
          ),
          validation: [
            {
              validator: (val) => !!val?.secondary,
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.required_validator',
            },
            {
              validator: (val) => /^\+?\d+$/.test(val.secondary),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.number.pattern_validator',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.DELEGATES,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.delegates',
        ),
        secondaryInputConfiguration: {
          type: 'none',
          validation: [
            {
              validator: () => this.delegates().some((delegate) => delegate.receiveCallsEnabled),
              message: 'tkey;microsoft_360.view.teams.call_forward_settings.target_type.delegates.delegates_validator',
            },
            {
              validator: () => this.forwardingTargetType() !== ForwardingTargetType.DELEGATES,
              message:
                'tkey;microsoft_360.view.teams.call_forward_settings.target_type.delegates.forwarding_to_delegates',
            },
          ],
        },
      },
      {
        value: UnansweredCallForwardingSettingsTargetType.CALL_PICKUP_GROUP,
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.target_type.call_pickup_group',
        ),
        disabled: this.pickupGroups().callPickupGroupMembers.length < 1,
        disabledTooltip: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.target_type.call_pickup_group.disabled_tooltip',
        ),
        secondaryInputConfiguration: {
          type: 'none',
        },
      },
    ];
    this.unansweredDelayOptions = [
      {
        value: '5',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '5' },
        ),
      },
      {
        value: '10',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '10' },
        ),
      },
      {
        value: '15',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '15' },
        ),
      },
      {
        value: '20',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '20' },
        ),
      },
      {
        value: '30',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '30' },
        ),
      },
      {
        value: '40',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '40' },
        ),
      },
      {
        value: '50',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '50' },
        ),
      },
      {
        value: '60',
        label: this.translateService.instant(
          'tkey;microsoft_360.view.teams.call_forward_settings.unanswered_rules.form.delay',
          { number: '60' },
        ),
      },
    ];
  }
}
